import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Heading,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Switch,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import { useMemo, useState } from 'react'
import { FiEdit, FiSave } from 'react-icons/fi'
import { useTranslation } from 'react-i18next'
import { ChainBadge } from '@/components/common/ChainBadge'
import { DataTable } from '@/components/common/DataTable'
import type { ActionButton, ColumnConfig } from '@/components/common/DataTable'
import { DropdownSelect } from '@/components/common/DropdownSelect'
import { CacheRefreshIconButton, RefreshIconButton } from '@/components/common/RefreshIconButton'
import { useToast } from '@/hooks/useToast'
import { useNetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import {
  useRefreshSystemChainsCache,
  useSystemChains,
  useUpdateSystemChain,
} from '@/hooks/useSystemConfigData'
import type { SystemChain } from '@/services/admin/systemService'

type ChainsTabProps = {
  cardBg: string
}

type ChainFormState = {
  name: string
  scanBaseUrl: string
  confirmationBlocks: string
  finalityOffsetMainnet: string
  finalityOffsetTestnet: string
  orderAmountHardFloorUsd: string
  isActive: boolean
  allowNewOrders: boolean
}

const toDateLabel = (value?: string): string => {
  if (!value) return '-'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  return parsed.toLocaleString()
}

const toDisplayChainName = (value: string): string => {
  const cleaned = value
    .replace(/\s*\((?:mainnet|main)\)\s*$/iu, '')
    .replace(/[\s_-]+(?:mainnet|main)\s*$/iu, '')
    .trim()
  return cleaned || value
}

const parseNullableNumber = (value: string): number | null => {
  const trimmed = value.trim()
  if (!trimmed) return null
  const parsed = Number(trimmed)
  if (!Number.isFinite(parsed)) {
    throw new Error('Invalid number')
  }
  return parsed
}

export function ChainsTab(props: ChainsTabProps) {
  const { t } = useTranslation()
  const toast = useToast()
  const { currentEnv } = useNetworkEnvironment()
  const { data, isFetching, refetch } = useSystemChains()
  const refreshMutation = useRefreshSystemChainsCache()
  const updateMutation = useUpdateSystemChain()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [editingRow, setEditingRow] = useState<SystemChain | null>(null)
  const [filters, setFilters] = useState({
    search: '',
    network: 'all' as 'all' | 'mainnet' | 'testnet',
    status: 'all' as 'all' | 'active' | 'inactive',
    limit: 100,
  })
  const [form, setForm] = useState<ChainFormState>({
    name: '',
    scanBaseUrl: '',
    confirmationBlocks: '',
    finalityOffsetMainnet: '',
    finalityOffsetTestnet: '',
    orderAmountHardFloorUsd: '',
    isActive: true,
    allowNewOrders: true,
  })

  const rows = data ?? []
  const networkOptions = useMemo(
    () => [
      { value: 'all', label: t('common.all') },
      {
        value: 'mainnet',
        label: t('systemConfig.general.networkMainnet', { defaultValue: 'Mainnet' }),
      },
      {
        value: 'testnet',
        label: t('systemConfig.general.networkTestnet', { defaultValue: 'Testnet' }),
      },
    ],
    [t]
  )
  const statusOptions = useMemo(
    () => [
      { value: 'all', label: t('common.all') },
      { value: 'active', label: t('common.active') },
      { value: 'inactive', label: t('common.inactive') },
    ],
    [t]
  )
  const filteredRows = useMemo(() => {
    const needle = filters.search.trim().toLowerCase()
    return rows
      .filter(row => {
        if (filters.network === 'all') return true
        return filters.network === 'testnet' ? row.isTestnet : !row.isTestnet
      })
      .filter(row => {
        if (filters.status === 'all') return true
        return filters.status === 'active' ? row.isActive : !row.isActive
      })
      .filter(row => {
        if (!needle) return true
        return (
          row.chainCode.toLowerCase().includes(needle)
          || row.name.toLowerCase().includes(needle)
          || row.compatibility.toLowerCase().includes(needle)
          || row.scanBaseUrl.toLowerCase().includes(needle)
        )
      })
      .slice(0, filters.limit)
  }, [filters, rows])

  const columns = useMemo<ColumnConfig<SystemChain>[]>(() => ([
    {
      key: 'chainCode',
      title: t('systemConfig.chains.columns.chainCode', { defaultValue: 'Chain' }),
      width: '11%',
      render: (_, row) => (
        <ChainBadge
          chainId={row.chainCode}
          chainCode={row.chainCode}
          chainName={row.name}
          chainNumericId={row.chainId}
          isTestnet={row.isTestnet}
        />
      ),
    },
    {
      key: 'name',
      title: t('common.name'),
      width: '20%',
      render: (_, row) => (
        <VStack align="start" spacing={1}>
          <HStack spacing={2}>
            <Text fontWeight="medium" fontSize="sm">
              {toDisplayChainName(row.name)}
            </Text>
            <Badge colorScheme={row.isTestnet ? 'purple' : 'blue'} fontSize="0.65rem">
              {row.isTestnet
                ? t('systemConfig.general.networkTestnet', { defaultValue: 'Testnet' })
                : t('systemConfig.general.networkMainnet', { defaultValue: 'Mainnet' })}
            </Badge>
            {currentEnv === 'mainnet' && row.isTestnet && (
              <Badge colorScheme="orange" variant="subtle" fontSize="0.65rem">
                {t('systemConfig.general.registryTestnet', {
                  defaultValue: 'Registry: Testnet',
                })}
              </Badge>
            )}
          </HStack>
        </VStack>
      ),
    },
    {
      key: 'chainId',
      title: t('systemConfig.chains.columns.chainId', { defaultValue: 'Chain ID' }),
      width: '9%',
      align: 'center',
      render: (_, row) => <Text fontSize="xs">{row.chainId}</Text>,
    },
    {
      key: 'compatibility',
      title: t('systemConfig.chains.columns.compatibility', { defaultValue: 'Compatibility' }),
      width: '9%',
      render: (_, row) => <Text fontSize="xs">{row.compatibility}</Text>,
    },
    {
      key: 'confirmationBlocks',
      title: t('systemConfig.chains.columns.confirmationBlocks', { defaultValue: 'Confirmations' }),
      width: '8%',
      align: 'center',
      render: (_, row) => <Text fontSize="xs">{row.confirmationBlocks}</Text>,
    },
    {
      key: 'isActive',
      title: t('common.status'),
      width: '7%',
      align: 'center',
      render: (_, row) => (
        <Badge colorScheme={row.isActive ? 'green' : 'gray'} fontSize="0.65rem">
          {row.isActive ? t('common.active') : t('common.inactive')}
        </Badge>
      ),
    },
    {
      key: 'allowNewOrders',
      title: t('systemConfig.chains.columns.allowNewOrders', {
        defaultValue: 'Allow new orders',
      }),
      width: '10%',
      align: 'center',
      render: (_, row) => (
        <Badge colorScheme={row.allowNewOrders ? 'green' : 'gray'} fontSize="0.65rem">
          {row.allowNewOrders ? t('common.active') : t('common.inactive')}
        </Badge>
      ),
    },
    {
      key: 'updatedAt',
      title: t('common.updatedAt', { defaultValue: 'Updated at' }),
      width: '13%',
      noWrap: true,
      render: (_, row) => <Text fontSize="xs">{toDateLabel(row.updatedAt)}</Text>,
    },
  ]), [currentEnv, t])

  const actions = useMemo(() => {
    return (row: SystemChain): ActionButton[] => [
      {
        label: t('common.edit'),
        icon: <FiEdit />,
        onClick: () => {
          setEditingRow(row)
          setForm({
            name: row.name,
            scanBaseUrl: row.scanBaseUrl,
            confirmationBlocks: String(row.confirmationBlocks),
            finalityOffsetMainnet:
              row.finalityOffsetMainnet === null ? '' : String(row.finalityOffsetMainnet),
            finalityOffsetTestnet:
              row.finalityOffsetTestnet === null ? '' : String(row.finalityOffsetTestnet),
            orderAmountHardFloorUsd: String(row.orderAmountHardFloorUsd),
            isActive: row.isActive,
            allowNewOrders: row.allowNewOrders,
          })
          onOpen()
        },
      },
    ]
  }, [onOpen, t])

  const handleSave = async () => {
    if (!editingRow) {
      return
    }
    try {
      const confirmationBlocks = Number(form.confirmationBlocks)
      const orderAmountHardFloorUsd = Number(form.orderAmountHardFloorUsd)
      if (!Number.isInteger(confirmationBlocks) || confirmationBlocks <= 0) {
        throw new Error('confirmationBlocks must be an integer > 0')
      }
      if (!Number.isFinite(orderAmountHardFloorUsd) || orderAmountHardFloorUsd < 0) {
        throw new Error('orderAmountHardFloorUsd must be >= 0')
      }

      await updateMutation.mutateAsync({
        id: editingRow.id,
        payload: {
          name: form.name.trim(),
          scanBaseUrl: form.scanBaseUrl.trim(),
          confirmationBlocks,
          finalityOffsetMainnet: parseNullableNumber(form.finalityOffsetMainnet),
          finalityOffsetTestnet: parseNullableNumber(form.finalityOffsetTestnet),
          orderAmountHardFloorUsd,
          isActive: form.isActive,
          allowNewOrders: form.allowNewOrders,
        },
      })
      toast.success({
        title: t('common.saveSuccess', { defaultValue: 'Saved' }),
      })
      onClose()
      await refetch()
    } catch (error) {
      toast.error({
        title: t('common.saveFailed', { defaultValue: 'Save failed' }),
        description: error instanceof Error ? error.message : String(error),
      })
    }
  }

  const handleRefreshCache = async () => {
    try {
      await refreshMutation.mutateAsync()
      toast.success({
        title: t('systemConfig.actions.refreshCacheSuccess', { defaultValue: 'Cache refreshed' }),
      })
    } catch (error) {
      toast.error({
        title: t('systemConfig.actions.refreshCacheFailed', { defaultValue: 'Cache refresh failed' }),
        description: error instanceof Error ? error.message : String(error),
      })
    }
  }

  return (
    <>
      <Card bg={props.cardBg}>
        <CardHeader>
          <Flex
            direction={{ base: 'column', xl: 'row' }}
            align={{ base: 'stretch', xl: 'end' }}
            justify="space-between"
            gap={3}
          >
            <HStack spacing={3} align="end" flexWrap={{ base: 'wrap', xl: 'nowrap' }} flex="1" minW={0}>
              <Heading size="sm" minW="fit-content">
                {t('systemConfig.tabs.chains', { defaultValue: 'chains' })}
              </Heading>
              <FormControl minW={{ base: '100%', md: '220px' }} maxW={{ xl: '220px' }}>
                <FormLabel fontSize="xs" mb={1}>
                  {t('common.search', { defaultValue: 'Search' })}
                </FormLabel>
                <Input
                  size="xs"
                  value={filters.search}
                  onChange={event => setFilters(prev => ({ ...prev, search: event.target.value }))}
                  placeholder={t('common.search', { defaultValue: 'Search' })}
                />
              </FormControl>
              <FormControl minW={{ base: '100%', md: '120px' }} maxW={{ xl: '120px' }}>
                <FormLabel fontSize="xs" mb={1}>
                  {t('systemConfig.runtimeConfig.network', { defaultValue: 'Network' })}
                </FormLabel>
                <DropdownSelect
                  value={filters.network}
                  options={networkOptions}
                  onChange={value =>
                    setFilters(prev => ({ ...prev, network: value as typeof filters.network }))
                  }
                  size="xs"
                />
              </FormControl>
              <FormControl minW={{ base: '100%', md: '120px' }} maxW={{ xl: '120px' }}>
                <FormLabel fontSize="xs" mb={1}>
                  {t('common.status')}
                </FormLabel>
                <DropdownSelect
                  value={filters.status}
                  options={statusOptions}
                  onChange={value =>
                    setFilters(prev => ({ ...prev, status: value as typeof filters.status }))
                  }
                  size="xs"
                />
              </FormControl>
              <FormControl minW={{ base: '100%', md: '100px' }} maxW={{ xl: '100px' }}>
                <FormLabel fontSize="xs" mb={1}>{t('common.limit')}</FormLabel>
                <DropdownSelect
                  value={String(filters.limit)}
                  options={[50, 100, 200, 500].map(limit => ({
                    value: String(limit),
                    label: String(limit),
                  }))}
                  onChange={value => setFilters(prev => ({ ...prev, limit: Number(value) }))}
                  size="xs"
                />
              </FormControl>
            </HStack>
            <HStack spacing={2} justify={{ base: 'flex-start', xl: 'flex-end' }} flexShrink={0}>
              <RefreshIconButton
                label={t('common.refresh')}
                onClick={() => refetch()}
                isLoading={isFetching}
              />
              <CacheRefreshIconButton
                label={t('systemConfig.actions.refreshCache', { defaultValue: 'Refresh Cache' })}
                onClick={handleRefreshCache}
                isLoading={refreshMutation.isPending}
              />
            </HStack>
          </Flex>
        </CardHeader>
        <CardBody
          pt={0}
          sx={{
            'th, td': {
              px: 2,
              py: 2,
            },
            th: {
              fontSize: 'xs',
            },
          }}
        >
          <DataTable<SystemChain>
            data={filteredRows}
            loading={isFetching}
            columns={columns}
            actions={actions}
            actionsColumnWidth="64px"
            showFilterBar={false}
            showRefresh={false}
            emptyMessage={t('common.noData', { defaultValue: 'No data' })}
          />
        </CardBody>
      </Card>

      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent className="system-config-dialog">
          <ModalHeader>{t('systemConfig.chains.edit', { defaultValue: 'Edit chain' })}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel>{t('common.name')}</FormLabel>
                <Input
                  value={form.name}
                  onChange={event => setForm(prev => ({ ...prev, name: event.target.value }))}
                />
              </FormControl>
              <FormControl>
                <FormLabel>{t('systemConfig.chains.fields.scanBaseUrl', { defaultValue: 'Scan base URL' })}</FormLabel>
                <Input
                  value={form.scanBaseUrl}
                  onChange={event => setForm(prev => ({ ...prev, scanBaseUrl: event.target.value }))}
                />
              </FormControl>
              <HStack align="start" spacing={4}>
                <FormControl>
                  <FormLabel>{t('systemConfig.chains.fields.confirmationBlocks', { defaultValue: 'Confirmation blocks' })}</FormLabel>
                  <Input
                    value={form.confirmationBlocks}
                    onChange={event => setForm(prev => ({ ...prev, confirmationBlocks: event.target.value }))}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>{t('systemConfig.chains.fields.orderAmountHardFloorUsd', { defaultValue: 'Order amount hard floor (USD)' })}</FormLabel>
                  <Input
                    value={form.orderAmountHardFloorUsd}
                    onChange={event =>
                      setForm(prev => ({ ...prev, orderAmountHardFloorUsd: event.target.value }))
                    }
                  />
                </FormControl>
              </HStack>
              <HStack align="start" spacing={4}>
                <FormControl>
                  <FormLabel>{t('systemConfig.chains.fields.finalityOffsetMainnet', { defaultValue: 'Finality offset mainnet' })}</FormLabel>
                  <Input
                    value={form.finalityOffsetMainnet}
                    onChange={event =>
                      setForm(prev => ({ ...prev, finalityOffsetMainnet: event.target.value }))
                    }
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>{t('systemConfig.chains.fields.finalityOffsetTestnet', { defaultValue: 'Finality offset testnet' })}</FormLabel>
                  <Input
                    value={form.finalityOffsetTestnet}
                    onChange={event =>
                      setForm(prev => ({ ...prev, finalityOffsetTestnet: event.target.value }))
                    }
                  />
                </FormControl>
              </HStack>
              <FormControl display="flex" alignItems="center">
                <FormLabel mb={0}>{t('common.active')}</FormLabel>
                <Switch
                  isChecked={form.isActive}
                  onChange={event => setForm(prev => ({ ...prev, isActive: event.target.checked }))}
                />
              </FormControl>
              <FormControl display="flex" alignItems="center">
                <FormLabel mb={0}>
                  {t('systemConfig.chains.fields.allowNewOrders', {
                    defaultValue: 'Allow new orders',
                  })}
                </FormLabel>
                <Switch
                  isChecked={form.allowNewOrders}
                  onChange={event =>
                    setForm(prev => ({ ...prev, allowNewOrders: event.target.checked }))
                  }
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={2}>
              <Button variant="ghost" onClick={onClose}>
                {t('common.cancel')}
              </Button>
              <Button
                colorScheme="admin"
                leftIcon={<FiSave />}
                onClick={handleSave}
                isLoading={updateMutation.isPending}
              >
                {t('common.save')}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}
